import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, FileSignature } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { Chip } from '../interviews/Chip'
import {
  useGetAgreementSignatures,
  useRecordAgreementSignature,
  useSetAgreementInForce,
  useWithdrawAgreementSignature,
} from '../../hooks/backend/drivers'
import { writeErrorMessage } from '../../utils/writeError'
import { readableDate } from '../../utils/coachingDates'
import { sastStamp } from '../../utils/drivingConduct'

// Who has signed the v6 agreement or Addendum No. 1, and when each came into
// force (db/migrations/0377).
//
// THIS IS THE GATE. A driver's driving is held to clauses 21.5 to 21.7 only from
// the later of the day they signed and the day the document came into force.
// Until both are here, everything on the Driving conduct page is coaching only.
// The penalties start after the induction at which the addendum is signed, so
// on the day this was built the list is meant to be empty.
//
// EVIDENCE IS REQUIRED, because a case would rest on it: the SignNow envelope,
// or where the signed copy is filed. Nothing is ever deleted. A correction needs
// a reason, a withdrawal needs a reason, and both are kept in the history.
//
// WHO MAY DO WHAT is decided in the database: recording needs an administrator
// or someone who may edit drivers; the in-force date needs an administrator.

type Agreement = { version: string; label: string; is_addendum: boolean; in_force_from: string | null; notes: string | null }
type Signature = { version: string; signed_on: string; evidence: string; recorded_by: string; recorded_at: string }
type DriverRow = {
  driver_id: string
  driver_name: string
  driver_status: string
  manager_name: string | null
  current_contract: string | null
  signatures: Signature[]
  gate_date: string | null
}
type History = { driver_name: string; version: string; action: string; signed_on: string | null; reason: string | null; by: string; at: string }
type Data = { can_record: boolean; can_set_dates: boolean; agreements: Agreement[]; drivers: DriverRow[]; history: History[] }

function todayIso(): string {
  return new Date(Date.now() + 2 * 3600 * 1000).toISOString().slice(0, 10)
}

export function AgreementSignaturesPanel() {
  const list = useGetAgreementSignatures<Data>()
  const record = useRecordAgreementSignature()
  const withdraw = useWithdrawAgreementSignature()
  const setDate = useSetAgreementInForce()
  const [q, setQ] = useState('')
  const [form, setForm] = useState<{ driver: DriverRow; version: string; existing: Signature | null } | null>(null)
  const [signedOn, setSignedOn] = useState(todayIso())
  const [evidence, setEvidence] = useState('')
  const [envelope, setEnvelope] = useState('')
  const [reason, setReason] = useState('')
  const [dateFor, setDateFor] = useState<Agreement | null>(null)
  const [inForce, setInForce] = useState('')
  const [busy, setBusy] = useState(false)

  const load = list.trigger
  const refresh = useCallback(() => load({}, { skipCache: true }), [load])
  useEffect(() => {
    refresh()
  }, [refresh])

  const d = list.data
  const agreements = d?.agreements ?? []
  const drivers = useMemo(() => {
    const s = q.trim().toLowerCase()
    return (d?.drivers ?? []).filter((r) => !s || r.driver_name.toLowerCase().includes(s) || (r.manager_name ?? '').toLowerCase().includes(s))
  }, [d, q])
  const signedCount = (d?.drivers ?? []).filter((r) => r.signatures.length > 0).length

  async function run(what: string, fn: () => Promise<unknown>, after: () => void) {
    setBusy(true)
    try {
      await fn()
      toast.success(what)
      after()
      await refresh()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  if (list.loading && !d) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    )
  }
  if (list.error && !d) {
    return (
      <p className="flex items-start gap-1.5 rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-800">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {list.error}
      </p>
    )
  }
  if (!d) return null

  return (
    <div className="space-y-6 px-4 py-4 text-sm">
      <section>
        <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold">
          <FileSignature className="h-4 w-4" /> The documents
        </h2>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {agreements.map((a) => (
            <div key={a.version} className="rounded-md border border-border p-3">
              <p className="font-medium">{a.label}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {a.in_force_from ? `In force from ${readableDate(a.in_force_from)}.` : 'No in-force date yet, so nobody is held to it.'}
              </p>
              {d.can_set_dates && (
                <Button size="sm" variant="outline" className="mt-2" onClick={() => { setDateFor(a); setInForce(a.in_force_from ?? ''); setReason('') }}>
                  {a.in_force_from ? 'Change the date' : 'Set the in-force date'}
                </Button>
              )}
            </div>
          ))}
        </div>
        {dateFor && (
          <fieldset className="mt-2 space-y-2 rounded-md border border-border p-3" disabled={busy}>
            <legend className="px-1 text-xs font-semibold">In-force date: {dateFor.label}</legend>
            <p className="text-xs text-muted-foreground">
              This switches the driving rules on for every driver who has signed it, from the later of this date and
              the day they signed. Nothing before it can become a case.
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div>
                <Label htmlFor="sig-inforce">In force from</Label>
                <Input id="sig-inforce" type="date" value={inForce} onChange={(e) => setInForce(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="sig-inforce-reason">Where the date comes from</Label>
                <Input id="sig-inforce-reason" placeholder="For example the induction on ..." value={reason} onChange={(e) => setReason(e.target.value)} />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() =>
                  run('In-force date saved', () => setDate.trigger({ version: dateFor.version, in_force_from: inForce || null, reason }), () => setDateFor(null))
                }
              >
                Save
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setDateFor(null)}>
                Cancel
              </Button>
            </div>
          </fieldset>
        )}
      </section>

      <section>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-semibold">Drivers</h2>
          <span className="text-xs text-muted-foreground">
            {signedCount} of {d.drivers.length} active or suspended drivers have a signature recorded
          </span>
          <Input className="ml-auto h-8 max-w-xs" placeholder="Find a driver or Driver Servant" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {!d.can_record && (
          <p className="mb-2 text-xs text-muted-foreground">
            Recording a signature needs an administrator or someone who may edit drivers. You can see the list.
          </p>
        )}
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-xs">
            <thead className="bg-muted text-left text-muted-foreground">
              <tr>
                <th className="px-2 py-1.5">Driver</th>
                {agreements.map((a) => (
                  <th key={a.version} className="px-2 py-1.5">{a.label}</th>
                ))}
                <th className="px-2 py-1.5">Rules apply from</th>
              </tr>
            </thead>
            <tbody>
              {drivers.map((r) => (
                <tr key={r.driver_id} className="border-t border-border">
                  <td className="px-2 py-1.5">
                    <div className="font-medium">{r.driver_name}</div>
                    <div className="text-muted-foreground">
                      {r.manager_name ?? 'No Driver Servant'}
                      {r.driver_status !== 'active' ? `, ${r.driver_status}` : ''}
                    </div>
                  </td>
                  {agreements.map((a) => {
                    const s = r.signatures.find((x) => x.version === a.version) ?? null
                    return (
                      <td key={a.version} className="px-2 py-1.5">
                        {s ? (
                          <div>
                            <Chip tone="green">Signed {readableDate(s.signed_on)}</Chip>
                            <div className="mt-0.5 text-muted-foreground">{s.evidence}</div>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">Not signed</span>
                        )}
                        {d.can_record && (
                          <div className="mt-1 flex gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-xs"
                              onClick={() => {
                                setForm({ driver: r, version: a.version, existing: s })
                                setSignedOn(s?.signed_on ?? todayIso())
                                setEvidence('')
                                setEnvelope('')
                                setReason('')
                              }}
                            >
                              {s ? 'Correct' : 'Record'}
                            </Button>
                            {s && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-xs"
                                onClick={() => {
                                  const why = window.prompt(`Why is ${r.driver_name}'s ${a.label} signature being withdrawn?`)
                                  if (why) run('Signature withdrawn', () => withdraw.trigger({ driver_id: r.driver_id, version: a.version, reason: why }), () => {})
                                }}
                              >
                                Withdraw
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    )
                  })}
                  <td className="px-2 py-1.5">
                    {r.gate_date ? <Chip tone="brand">{readableDate(r.gate_date)}</Chip> : <span className="text-muted-foreground">Coaching only</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {form && (
          <fieldset className="mt-2 space-y-2 rounded-md border border-border p-3" disabled={busy}>
            <legend className="px-1 text-xs font-semibold">
              {form.existing ? 'Correct' : 'Record'}: {form.driver.driver_name},{' '}
              {agreements.find((a) => a.version === form.version)?.label}
            </legend>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <div>
                <Label htmlFor="sig-on">Date signed</Label>
                <Input id="sig-on" type="date" value={signedOn} onChange={(e) => setSignedOn(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="sig-env">SignNow envelope (if signed there)</Label>
                <Input id="sig-env" value={envelope} onChange={(e) => setEnvelope(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="sig-ev">Or where the signed copy is filed</Label>
                <Input id="sig-ev" placeholder="For example the induction pack, 12 Oct" value={evidence} onChange={(e) => setEvidence(e.target.value)} />
              </div>
            </div>
            {form.existing && (
              <div>
                <Label htmlFor="sig-why">Why it is being corrected</Label>
                <Input id="sig-why" value={reason} onChange={(e) => setReason(e.target.value)} />
              </div>
            )}
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() =>
                  run(
                    form.existing ? 'Signature corrected' : 'Signature recorded',
                    () =>
                      record.trigger({
                        driver_id: form.driver.driver_id,
                        version: form.version,
                        signed_on: signedOn,
                        evidence: evidence || null,
                        signnow_envelope_id: envelope || null,
                        reason: reason || null,
                      }),
                    () => setForm(null),
                  )
                }
              >
                Save
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setForm(null)}>
                Cancel
              </Button>
            </div>
          </fieldset>
        )}
      </section>

      {d.history.length > 0 && (
        <section>
          <h2 className="mb-2 text-sm font-semibold">History</h2>
          <ul className="space-y-1 text-xs">
            {d.history.map((h, i) => (
              <li key={i}>
                <span className="text-muted-foreground">{sastStamp(h.at)}</span> {h.by} {h.action} {h.driver_name}'s{' '}
                {h.version === 'v6' ? 'v6' : 'Addendum No. 1'} signature
                {h.signed_on ? ` (${readableDate(h.signed_on)})` : ''}
                {h.reason ? `: ${h.reason}` : ''}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}
